import api from './api'

export type ClaimStatus =
  | 'pending'
  | 'submitted'
  | 'accepted'
  | 'rejected'
  | 'paid'
  | 'provider-payout'
export interface ClaimParams {
  search?: string
  startDate?: string
  endDate?: string
  contractId?: string
  status?: ClaimStatus | ''
  patientId?: string
  therapistId?: string
  payoutStatus?: boolean
  page?: number
  limit?: number
}

export interface Claim {
  _id: string
  claim_id?: string
  claimId?: string
  // New format: contractId is an object
  contractId?: { contract_ids: string; _id: string; [key: string]: any }
  // Old format: contract_id can be string or object
  contract_id?: string | { contract_ids: string; _id: string }
  contract_ids?: string
  // New format: sessionIds is a string at top level
  sessionIds?: string
  // New format: sessionId is an object
  sessionId?: {
    _id?: string
    session_ids?: string
    approved_amount?: number
    co_pay?: number
    [key: string]: any
  } | null
  // Old format: session_id and session_ids
  session_id?: string
  session_ids?: string
  // Patient fields
  patientId?: string | { firstName?: string; lastName?: string; [key: string]: any }
  patientFirstName?: string
  patientLastName?: string
  patient_name?: string
  patientName?: string
  // Therapist fields
  therapistId?: string | { firstName?: string; lastName?: string; [key: string]: any }
  // Service fields
  service_id?: string | { name?: string; cpt_code?: any[]; [key: string]: any }
  serviceName?: string
  // Payer fields
  payer?: string
  payer_name?: string
  payerName?: string
  payerId?: string
  // Amount fields
  claimed_amount?: number
  claimedAmount?: number
  approved_amount?: number
  approvedAmount?: number
  co_pay?: number
  coPay?: number
  coPayAmount?: number
  // Date fields
  submission_date?: string
  submissionDate?: string
  pre_authorization_date?: string
  preAuthorizationDate?: string
  serviceStartDate?: string
  serviceEndDate?: string
  // Status fields
  claim_status?: ClaimStatus
  status?: ClaimStatus
  isPayoutCreated?: boolean
  // Other fields
  invoice_id?: string
  invoiceId?: string
  createdAt?: string
  updatedAt?: string
  [key: string]: any // Allow additional fields
}

export interface ClaimsResponse {
  results?: Claim[]
  data?: Claim[]
  total?: number
  totalRecords?: number
  totalAmount?: number
  totalProviderPayout?: number
  currentPage?: number
  totalPages?: number
}

export interface ClaimsSummaryResponse {
  pending: number
  submitted: number
  accepted: number
  paid: number
  rejected: number
  totalProviderPayout: number
}

export interface ContractDropdownOption {
  _id: string
  contract_ids: string
}

export const getClaims = async (
  params: ClaimParams
): Promise<ClaimsResponse> => {
  const response = await api.get<ClaimsResponse>('/insurance-claims', {
    params: {
      search: params.search || '',
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      contractId: params.contractId || '',
      status: params.status || '',
      patientId: params.patientId || undefined,
      therapistId: params.therapistId || undefined,
      payoutStatus:
        params.payoutStatus === undefined ? undefined : params.payoutStatus,
      page: params.page || 1,
      limit: params.limit || 10,
    },
  })

  // Handle different response structures
  const data = response.data
  if (Array.isArray(data)) {
    return { results: data, data, total: data.length }
  }
  if (data.results) {
    return data
  }
  if (data.data) {
    return {
      results: data.data,
      data: data.data,
      total: data.total || data.data.length,
    }
  }
  return { results: [], data: [], total: 0 }
}

export const getClaimsSummary = async (): Promise<ClaimsSummaryResponse> => {
  const response = await api.get<ClaimsSummaryResponse>(
    '/insurance-claims/summary'
  )
  return response.data
}

export const getContractDropdowns = async (
  search?: string
): Promise<ContractDropdownOption[]> => {
  const params: Record<string, any> = {}
  if (search?.trim()) {
    params.search = search.trim()
  }
  const response = await api.get<ContractDropdownOption[]>(
    '/contracts/dropdown/contracts',
    { params }
  )
  return Array.isArray(response.data) ? response.data : []
}

export const exportClaimsCSV = async (
  params: Omit<ClaimParams, 'page' | 'limit'>
): Promise<Blob> => {
  try {
    const response = await api.get<Blob>('/insurance-claims/export', {
      params: {
        search: params.search || '',
        startDate: params.startDate || '',
        endDate: params.endDate || '',
        contractId: params.contractId || '',
        status: params.status || '',
        patientId: params.patientId || undefined,
        therapistId: params.therapistId || undefined,
      },
      responseType: 'blob',
    })
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      throw new Error('Export functionality is not available yet')
    }
    throw error
  }
}

